import { isFiatMarketCode } from "@/utils/fiatMarket";
import { resolveAssetFullName } from "@/components/Pages/Market/MarketsPc/coinFullNames";
import { parseSymbolParts } from "@/components/Pages/Market/MarketsPc/utils";

const GLOBAL_FIAT_PLATE_ID = 258;

function getCfdQuoteCurrency(symbol) {
  if (!symbol) return "";
  const parts = String(symbol).split("_");
  if (parts.length < 2) return "";
  return String(parts[1]).toUpperCase();
}

function isQuoteFiatByCurrencyPlates(quote, currencyMap, currencyList) {
  if (!quote) return false;
  const upper = String(quote).toUpperCase();
  const c =
    (currencyMap &&
      (currencyMap[upper] ||
        currencyMap[String(quote).toLowerCase()] ||
        currencyMap[quote])) ||
    null;
  if (c) {
    const plates = Array.isArray(c.plates) ? c.plates : [];
    if (plates.length) return plates.some((p) => Number(p) === GLOBAL_FIAT_PLATE_ID);
  }
  if (Array.isArray(currencyList)) {
    const item = currencyList.find(
      (x) => String(x.currency || x.coinName || "").toUpperCase() === upper
    );
    if (item) {
      const plates = Array.isArray(item.plates) ? item.plates : [];
      return plates.some((p) => Number(p) === GLOBAL_FIAT_PLATE_ID);
    }
  }
  return false;
}

/** 与 HomeSearchOverlay / MarketList 法币 CFD 筛选一致 */
export function passesSearchCfdFiatFilter(symbol, marketEntry, currencyMap, currencyList) {
  if (!marketEntry) return false;
  const normalizedType = String(marketEntry.type || "").toUpperCase();
  if (normalizedType !== "CFD") return true;
  const quote = getCfdQuoteCurrency(symbol);
  if (!quote) return true;
  if (quote === "USDT") return true;
  if (isFiatMarketCode(quote)) return true;
  if (isQuoteFiatByCurrencyPlates(quote, currencyMap, currencyList)) return false;
  return true;
}

export function isSearchableMarketEntry(symbol, config, fund) {
  if (!config || config.state === "DELISTED" || config.displayLevel === "NONE") return false;
  const { currencyMap, currencyList } = fund || {};
  return passesSearchCfdFiatFilter(symbol, config, currencyMap, currencyList);
}

function normalizeKeyword(keyword) {
  return String(keyword || "")
    .trim()
    .toLowerCase()
    .replace(/\//g, "_");
}

function matchesMarketKeyword(symbol, keyword, fund) {
  if (!keyword) return true;
  const k = normalizeKeyword(keyword);
  const { base, quote } = parseSymbolParts(symbol);
  const baseUpper = base.toUpperCase();
  const quoteUpper = quote.toUpperCase();
  const symLower = String(symbol || "").toLowerCase();
  const name = resolveAssetFullName(base, fund).toLowerCase();

  return (
    baseUpper.toLowerCase().includes(k) ||
    quoteUpper.toLowerCase().includes(k) ||
    symLower.includes(k) ||
    name.includes(k)
  );
}

function quotePreferenceRank(quote) {
  const u = String(quote || "").toUpperCase();
  if (u === "USDT") return 0;
  if (u === "USDC") return 1;
  return 2;
}

function isCfdMarketSymbol(symbol, config) {
  const s = String(symbol || "").toLowerCase();
  if (s.endsWith("_cfd")) return true;
  return String(config?.type || "").toUpperCase() === "CFD";
}

/** 将任意 marketName 解析为 marketConfig 中的 CFD 交易对 */
export function normalizeToCfdSymbol(symbol, marketConfig) {
  const raw = String(symbol || "").trim().toLowerCase();
  if (!raw || !marketConfig) return raw;

  if (raw.endsWith("_cfd") && marketConfig[raw]) return raw;

  const direct = raw.endsWith("_cfd") ? raw : `${raw.replace(/_cfd$/i, "")}_cfd`;
  if (marketConfig[direct]) return direct;

  const { base, quote } = parseSymbolParts(raw);
  if (base && quote) {
    const candidate = `${base}_${quote}_cfd`;
    if (marketConfig[candidate]) return candidate;
  }

  if (base) {
    const candidates = Object.keys(marketConfig).filter((sym) => {
      if (!sym.endsWith("_cfd")) return false;
      const cfg = marketConfig[sym];
      if (!isSearchableMarketEntry(sym, cfg, null)) return false;
      return parseSymbolParts(sym).base.toLowerCase() === base.toLowerCase();
    });
    if (candidates.length) {
      candidates.sort(
        (a, b) =>
          quotePreferenceRank(parseSymbolParts(a).quote) - quotePreferenceRank(parseSymbolParts(b).quote)
      );
      return candidates[0];
    }
  }

  return raw.endsWith("_cfd") ? raw : "";
}

/** spikex /search：同一 base 只保留一条，优先 USDT 计价 */
export function dedupeByBaseSymbol(records) {
  const best = new Map();
  for (const item of records) {
    const { base, quote } = parseSymbolParts(item.s);
    if (!base) continue;
    const existing = best.get(base);
    if (!existing) {
      best.set(base, item);
      continue;
    }
    const curRank = quotePreferenceRank(quote);
    const prevRank = quotePreferenceRank(parseSymbolParts(existing.s).quote);
    if (curRank < prevRank) best.set(base, item);
  }
  return [...best.values()];
}

/**
 * @param {object} opts
 * @param {object} opts.marketConfig
 * @param {object[]} [opts.tickerRecords]
 * @param {string} opts.keyword
 * @param {object} [opts.fund]
 * @param {boolean} [opts.dedupeByBase]
 * @param {boolean} [opts.cfdOnly]
 * @param {number} [opts.limit]
 */
export function searchMarketsFromConfig({
  marketConfig,
  tickerRecords = [],
  keyword,
  fund,
  dedupeByBase = false,
  cfdOnly = false,
  limit,
}) {
  if (!marketConfig || !normalizeKeyword(keyword)) return [];

  const tickerMap = {};
  (tickerRecords || []).forEach((row) => {
    if (row?.s) tickerMap[row.s] = row;
  });

  let hits = Object.keys(marketConfig)
    .filter((symbol) => {
      const config = marketConfig[symbol];
      if (cfdOnly && !isCfdMarketSymbol(symbol, config)) return false;
      if (!isSearchableMarketEntry(symbol, config, fund)) return false;
      return matchesMarketKeyword(symbol, keyword, fund);
    })
    .map((symbol) => {
      const cfdSymbol = cfdOnly ? normalizeToCfdSymbol(symbol, marketConfig) || symbol : symbol;
      return tickerMap[cfdSymbol] || tickerMap[symbol] || { s: cfdSymbol };
    });

  hits.sort((a, b) => {
    const k = normalizeKeyword(keyword);
    const aBase = parseSymbolParts(a.s).base.toUpperCase().toLowerCase();
    const bBase = parseSymbolParts(b.s).base.toUpperCase().toLowerCase();
    const aBaseHit = aBase.includes(k);
    const bBaseHit = bBase.includes(k);
    if (aBaseHit && !bBaseHit) return -1;
    if (!aBaseHit && bBaseHit) return 1;
    return quotePreferenceRank(parseSymbolParts(a.s).quote) - quotePreferenceRank(parseSymbolParts(b.s).quote);
  });

  if (dedupeByBase) hits = dedupeByBaseSymbol(hits);
  if (typeof limit === "number" && limit > 0) hits = hits.slice(0, limit);
  return hits;
}

/** 行情列表内联搜索：在已有 records 上按 symbol / name 过滤 */
export function filterRecordsByKeyword(records, keyword, fund) {
  const k = normalizeKeyword(keyword);
  if (!k) return records;
  return (records || []).filter((item) => matchesMarketKeyword(item.s, k, fund));
}

export function resolveSearchTradeHref(symbol, getUrl, marketConfig) {
  const cfd = normalizeToCfdSymbol(symbol, marketConfig) || String(symbol || "").toLowerCase();
  if (!cfd) return getUrl("/market");
  return getUrl(`/futures/detail/${cfd}`);
}

export const DEFAULT_TRENDING_BASES = ["btc", "eth"];

export function resolveTrendingSymbols(marketConfig, bases = DEFAULT_TRENDING_BASES) {
  if (!marketConfig) return [];
  return bases
    .map((base) => {
      const candidates = Object.keys(marketConfig).filter((sym) => {
        if (!sym.endsWith("_cfd")) return false;
        const cfg = marketConfig[sym];
        if (!isSearchableMarketEntry(sym, cfg, null)) return false;
        return parseSymbolParts(sym).base.toLowerCase() === base.toLowerCase();
      });
      if (!candidates.length) return null;
      const sorted = [...candidates].sort(
        (a, b) =>
          quotePreferenceRank(parseSymbolParts(a).quote) - quotePreferenceRank(parseSymbolParts(b).quote)
      );
      return sorted[0];
    })
    .filter(Boolean);
}
